import React, { useState } from "react";
import axios from "axios";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label
} from "./components/ui";

const resolveApiBase = () => {
  const env = process.env.REACT_APP_API_URL;
  if (env && env.trim().length > 0) {
    const trimmed = env.trim().replace(/\/+$/, "");
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  }
  if (typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) {
    return "http://127.0.0.1:8000";
  }
  return "/api";
};
const API_URL = resolveApiBase();

const Login = ({ setToken, setUsername: setGlobalUsername }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [secretCode, setSecretCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    const url = isRegistering ? `${API_URL}/auth/register` : `${API_URL}/auth/token`;

    try {
        if (isRegistering) {
            await axios.post(url, { username, password, secret_code: secretCode });
        setError("Registration successful! Please log in.");
            setIsRegistering(false);
        } else {
            const formData = new URLSearchParams();
        formData.append("username", username);
        formData.append("password", password);
        const response = await axios.post(url, formData, {
          headers: { "Content-Type": "application/x-www-form-urlencoded" }
        });
            setToken(response.data.access_token);
        localStorage.setItem("username", username);
        setGlobalUsername?.(username);
        }
    } catch (err) {
      setError(err.response?.data?.detail || "An error occurred.");
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-amber-50 via-rose-50 to-slate-50 px-4 py-8">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
            NS
          </div>
          <h1 className="text-2xl font-semibold text-foreground">
            {isRegistering ? "Create your Nestos account" : "Welcome back to Nestos"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isRegistering
              ? "Sign up to centralize invoice downloads across SellerFlash, SellerRunning and Amazon."
              : "Sign in to continue automating your invoice workflow."}
          </p>
        </div>

        <Card className="border-border/60 bg-card/80 shadow-xl">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-xl font-semibold">
              {isRegistering ? "Create account" : "Sign in"}
            </CardTitle>
            <CardDescription>
              {isRegistering ? "We’ll need a secret code for registration." : "Enter your credentials to continue."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {error && (
              <Alert variant={error.includes("successful") ? "default" : "destructive"}>
                <AlertTitle>{error.includes("successful") ? "Success" : "Attention"}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
              id="username"
              type="text" 
              placeholder="Enter your username" 
              value={username} 
                  onChange={(event) => setUsername(event.target.value)}
              required 
              disabled={loading}
            />
          </div>
          
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
              id="password"
              type="password" 
              placeholder="Enter your password" 
              value={password} 
                  onChange={(event) => setPassword(event.target.value)}
              required 
              disabled={loading}
            />
          </div>

          {isRegistering && (
                <div className="space-y-2">
                  <Label htmlFor="secret_code">Registration secret code</Label>
                  <Input
                id="secret_code"
                type="password"
                placeholder="Enter registration secret code"
                value={secretCode}
                    onChange={(event) => setSecretCode(event.target.value)}
                required
                disabled={loading}
              />
            </div>
          )}
          
              <Button type="submit" className="w-full gap-2" disabled={loading}>
                {loading ? <Loader /> : null}
                {loading ? "Please wait..." : isRegistering ? "Create account" : "Sign in"}
              </Button>
        </form>
        
            <div className="text-center text-sm text-muted-foreground">
              {isRegistering ? "Already have an account?" : "Don't have an account?"}
            <button 
                className="ml-1 font-medium text-primary transition hover:underline"
                onClick={() => {
                  setIsRegistering(!isRegistering);
                  setError("");
                }}
              type="button"
            >
                {isRegistering ? "Sign in" : "Create account"}
            </button>
        </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Loader = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle className="opacity-25" cx="12" cy="12" r="10" />
    <path className="opacity-75" d="M4 12a8 8 0 018-8" />
  </svg>
);

export default Login;